import ReactECharts from "echarts-for-react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";

// Component for horizontal bar chart using ECharts
const HorizontalBarChart = ({
  data,
  colors,
  maxItems = 10,
  usageLabel,
  "data-testid": dataTestId = "horizontal-bar-chart",
}) => {
  const { t } = useTranslation();
  const { colors: themeColors } = useTheme();
  const chartRef = useRef(null);

  const barColors = [
    "#FF7070",
    "#FFAC80",
    "#FFE48C",
    "#D1A5FF",
    "#99FFF8",
    "#99FFB1",
    "#FFAEF1",
    "#98B5FF",
    "#FFB5B6",
    "#FF9F63",
  ];

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const sortedData = [...data].sort((a, b) => (b.count || 0) - (a.count || 0));
    return sortedData.slice(0, maxItems).map((item, index) => ({
      name: item.name || "",
      value: item.count || 0,
      itemStyle: {
        color: barColors[index % barColors.length],
      },
    }));
  }, [data, maxItems]);

  const maxValue = Math.max(...chartData.map((item) => item.value));
  const labelColor = colors?.chartTextColor || themeColors.chartTextColor;

  const option = useMemo(
    () => ({
      backgroundColor: "transparent",
      grid: {
        left: "10rem",
        right: "1rem",
        top: "0.5rem",
        bottom: "0.5rem",
        containLabel: true,
      },
      tooltip: {
        trigger: "item",
        backgroundColor: themeColors.chartTooltipBackgroundColor,
        borderColor: "transparent",
        borderRadius: "0.75rem",
        textStyle: {
          color: themeColors.chartTooltipTextColor,
          fontSize: "0.875rem",
        },
        extraCssText: "border-radius: 0.75rem !important;",
        formatter: function (params) {
          const label = usageLabel ? t(usageLabel) : t("status.component-usage");
          return `
          <div style="padding: 0.375rem;">
            <div style="margin-bottom: 0.375rem; font-weight: 600;">${params.name}</div>
            <div style="display: flex; align-items: center; gap: 0.375rem;">
              <div style="width: 0.625rem; height: 0.625rem; background-color: ${params.color}; border-radius: 0.125rem;"></div>
              <span>${label}: ${params.value}</span>
            </div>
          </div>
        `;
        }
      },
      xAxis: {
        type: "value",
        show: false,
        max: maxValue,
      },
      yAxis: {
        type: "category",
        data: chartData.map((item) => item.name),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          show: true,
          color: labelColor,
          fontSize: 12,
          fontWeight: 500,
          formatter: function (value) {
            if (value.length > 15) {
              return value.substring(0, 15) + "...";
            }
            return value;
          },
          margin: 6,
        },
        inverse: true,
      },
      series: [
        {
          type: "bar",
          data: chartData.map((item, index) => ({
            ...item,
            itemStyle: {
              color: item.itemStyle.color,
              borderRadius: [35, 35, 35, 35],
            },
          })),
          barWidth: 28,
          barMinHeight: 36,
          barGap: 24,
          label: {
            show: true,
            position: "right",
            distance: -18,
            formatter: function (params) {
              return params.value;
            },
            backgroundColor: themeColors.chartLabelBackgroundColor,
            borderRadius: [35, 30, 30, 35],
            padding: [3, 16],
            fontSize: 10,
            fontWeight: "bold",
            color: colors.badgeTextColor,
            width: 0,
            height: 18,
            align: "center",
            verticalAlign: "middle",
            lineHeight: 18,
            borderColor: themeColors.chartLabelBorderColor,
            borderWidth: 0.25,
          },
          animation: true,
          animationDuration: 800,
          animationEasing: "cubicOut",
          animationDelay: function (idx) {
            return idx * 50;
          },
        },
      ],
      animation: true,
      animationDuration: 800,
      animationEasing: "cubicOut",
    }),
    [chartData, maxValue, labelColor, t, themeColors],
  );

  useEffect(() => {
    const timer = setTimeout(() => { }, 100);

    return () => clearTimeout(timer);
  }, [data]);

  useEffect(() => {
    if (chartRef.current && chartRef.current.getEchartsInstance()) {
      const chartInstance = chartRef.current.getEchartsInstance();

      chartInstance.setOption(
        {
          series: [
            {
              data: chartData.map((item, index) => ({
                ...item,
                itemStyle: {
                  color: item.itemStyle.color,
                  borderRadius: [35, 35, 35, 35],
                },
              })),
            },
          ],
        },
        false,
      );
    }
  }, [data, maxItems]);

  if (!data || data.length === 0) {
    return (
      <div
        className={`flex h-64 items-center justify-center ${colors.textSecondary}`}
        data-testid={`${dataTestId}-empty`}
      >
        <p data-testid={`${dataTestId}-empty-message`}>{t("common.no-data-to-display")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={dataTestId}>
      <div data-testid={`${dataTestId}-chart`}>
      <ReactECharts
        ref={chartRef}
        option={option}
        style={{
          height: `${chartData.length * 52}px`,
          width: "100%",
        }}
        opts={{ renderer: "canvas" }}
      />
      </div>
    </div>
  );
};

export default HorizontalBarChart;
